import * as React from "react"
import { cn } from "@/lib/utils"
import { Tooltip as TooltipPrimitive } from "radix-ui"

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

/**
 * How the tooltip surface paints.
 *
 * `default` is the dark, compact tooltip for short one-line hints. `panel` is the
 * light card surface — the same `--panel`/`--line`/`--radius-card`/`--shadow-drawer`
 * tokens the Calendar's milestone-cluster popover uses — for the richer, multi-line
 * hover content of bars and milestone markers. No new tokens are introduced; the
 * variant only recombines existing ones, and it carries no arrow so it reads as the
 * same floating card as the popover.
 */
type TooltipContentVariant = "default" | "panel"

const TOOLTIP_ANIMATION =
  "origin-(--radix-tooltip-content-transform-origin) animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"

const TOOLTIP_SURFACE: Record<TooltipContentVariant, string> = {
  default: "w-fit rounded-md bg-foreground px-3 py-1.5 text-xs text-balance text-background",
  panel:
    "border-line bg-panel text-txt w-fit rounded-[var(--radius-card)] border p-2 shadow-[var(--shadow-drawer)]",
}

function TooltipContent({
  className,
  sideOffset = 0,
  variant = "default",
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  variant?: TooltipContentVariant
}) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn("z-50", TOOLTIP_ANIMATION, TOOLTIP_SURFACE[variant], className)}
        {...props}
      >
        {children}
        {variant === "default" ? (
          <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-foreground fill-foreground" />
        ) : null}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
export type { TooltipContentVariant }
